import React, { ReactElement, useMemo, useRef, useCallback, useEffect, useState } from "react";
import moment from "moment";
import { DataList, DataListColumn, DataListFetchHandler, CreateButtonProps } from "src/components/DataList";
import {
    OperationalTargetsListViewProps,
    OperationalTargetsListViewState,
} from "./OperationalTargetsListView.types";
import { localize } from "src/l10n";
import SpintrList from "src/ui/components/SpintrList/SpintrList";
import { useSelector, useDispatch } from "react-redux";
import { queryOperationalTargets } from "src/redux";
import PageHeader, { IContentHeaderButton } from "src/ui/components/PageHeader";
import { OperationalTargetFormDrawer } from "src/components";
import { UnstyledButton } from "src/ui";

/**
 * OperationalTargetsListView component
 *
 * @param {OperationalTargetsListViewProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function OperationalTargetsListView({
}: OperationalTargetsListViewProps): ReactElement | null {
    const dispatch = useDispatch();
    const listRef = useRef<SpintrList>(null);
    const [state, setState] = useState<OperationalTargetsListViewState>({
        drawerOpen: false,
    });

    const {
        items: operationalTargets,
        isLoading,
        hasLoaded,
    } = useSelector<Spintr.AppState, Spintr.AppState["qualityHub"]["operationalTargets"]>(
        (appState) => ({ ...appState.qualityHub.operationalTargets }),
    );

    const renderCell = useCallback((el: ReactElement | null, item: Spintr.OperationalTarget) => (
        <UnstyledButton<Spintr.OperationalTarget> onClick={() => setState((s) => ({
            ...s,
            drawerOpen: true,
            selectedId: item.id,
        }))}>
            {el}
        </UnstyledButton>
    ), []);

    const columns = useMemo<DataListColumn[]>(() => [
        {
            fieldName: "startDate",
            key: "startDate",
            isSortable: false,
            maxWidth: 120,
            minWidth: 0,
            name: localize("Startdatum"),
            onRender: (item) => renderCell((
                <span>{moment(item.startDate).format("YYYY-MM-DD")}</span>
            ), item),
        },
        {
            fieldName: "endDate",
            key: "endDate",
            isSortable: false,
            maxWidth: 120,
            minWidth: 0,
            name: localize("Slutdatum"),
            onRender: (item) => !item?.endDate ? null : renderCell((
                <span>{moment(item.endDate).format("YYYY-MM-DD")}</span>
            ), item),
        },
        {
            fieldName: "target",
            key: "target",
            isSortable: false,
            minWidth: 0,
            name: localize("OPS_TARGET_TARGET"),
            onRender: (item) => renderCell((
                <span>{item.target}</span>
            ), item),
        },
        {
            fieldName: "focus",
            key: "focus",
            isSortable: false,
            minWidth: 0,
            name: localize("OPS_TARGET_FOCUS"),
            onRender: (item) => renderCell((
                <span>{item.focus}</span>
            ), item),
        },
    ], [renderCell]);

    const fetch = useCallback<DataListFetchHandler>(
        () => new Promise((resolve) => resolve({
            data: operationalTargets,
            total: operationalTargets.length,
        })), [operationalTargets]);

    const buttons = useMemo<IContentHeaderButton[]>(() => [
        { className: "commandBarAddButton commandBarButton primaryBGColor primaryTextContrast"
        , iconProps: { iconName: "Add" }
        , key: "create-button"
        , onClick: () => setState((s) => ({ ...s, drawerOpen: true }))
        , text: localize("SkapaNy")
        , theme: "primary"
        }
    ], [setState]);

    const onDrawerDismissed = useCallback(() => {
        setState((s) => ({ ...s, drawerOpen: false, selectedId: undefined }));
        listRef?.current?.reFetch?.();
    }, [listRef, setState]);

    const l10n = useMemo(() => ({
        lists: localize("OPS_TARGETS"),
    }), []);

    useEffect(() => {
        if (hasLoaded || isLoading) {
            return;
        }

        dispatch(queryOperationalTargets());
    }, [hasLoaded, isLoading, dispatch]);

    useEffect(() => {
        if (!listRef.current?.reFetch) {
            return;
        }

        listRef.current.reFetch();
    }, [operationalTargets, listRef]);

    const opsTarget = operationalTargets.find(t => t.id === state.selectedId);

    return (
        <div id="OperationalTargetsListView">
            <PageHeader buttons={buttons}
                        displaySearch={false}
                        title={l10n.lists}/>
            <DataList columns={columns}
                      disableCommandBar={true}
                      disableSearch={true}
                      fetch={fetch}
                      isLoading={isLoading}
                      orderByColumn="startDate"
                      ref={listRef} />
            <OperationalTargetFormDrawer onDismiss={onDrawerDismissed}
                                         open={state.drawerOpen}
                                         operationalTarget={opsTarget} />
        </div>
    );
}

export default OperationalTargetsListView;

